'use client';

import type { EmployeeWithStats } from './types';
import Icon from '@/components/ui/AppIcon';

interface HrKpiStripProps {
  employees: EmployeeWithStats[];
  pendingApprovalsCount: number;
}

const HrKpiStrip = ({ employees, pendingApprovalsCount }: HrKpiStripProps) => {
  const total = employees.length;
  const active = employees.filter(e => e.employment_type !== 'terminated' && !e.termination_date).length;
  const departments = new Set(employees.map(e => e.department).filter(Boolean));

  const expiringCertsTotal = employees.reduce((sum, e) => sum + (e.expiring_certs_count ?? 0), 0);
  const employeesWithExpiringCerts = employees.filter(e => (e.expiring_certs_count ?? 0) > 0).length;

  const reviewsDueCount = employees.filter(e => (e.released_reviews_count ?? 0) === 0).length;

  // On Leave Today — placeholder, no data yet (PTO requests come later)
  const onLeaveToday = 0;

  // Avg rating across all latest released reviews
  const ratedEmployees = employees.filter(e => typeof e.latest_review_rating === 'number');
  const avgRating = ratedEmployees.length > 0
    ? (ratedEmployees.reduce((sum, e) => sum + (e.latest_review_rating ?? 0), 0) / ratedEmployees.length).toFixed(1)
    : '—';

  const tiles = [
    { label: 'TOTAL EMPLOYEES', value: total,              sub: `${active} active`,                                                                    icon: 'UsersIcon',                    color: '#1C2E5E', bg: '#eff6ff' },
    { label: 'ACTIVE',          value: active,             sub: `${total - active} inactive`,                                                          icon: 'UserPlusIcon',                 color: '#16a34a', bg: '#dcfce7' },
    { label: 'DEPARTMENTS',     value: departments.size,   sub: 'Across the company',                                                                  icon: 'BuildingOffice2Icon',           color: '#7c3aed', bg: '#ede9fe' },
    { label: 'CERTS EXPIRING',  value: expiringCertsTotal, sub: expiringCertsTotal > 0 ? `${employeesWithExpiringCerts} employees` : 'All current',    icon: 'ShieldExclamationIcon',        color: '#F5A623', bg: '#fef3c7' },
    { label: 'REVIEWS DUE',     value: reviewsDueCount,    sub: reviewsDueCount > 0 ? 'Action required' : 'All current',                               icon: 'ClipboardDocumentCheckIcon',   color: '#dc2626', bg: '#fee2e2' },
    { label: 'AVG RATING',      value: avgRating,          sub: ratedEmployees.length > 0 ? `${ratedEmployees.length} reviewed` : 'No data',           icon: 'StarIcon',                     color: '#E89A3C', bg: '#e0f2fe' },
    { label: 'PENDING APPROVALS', value: pendingApprovalsCount, sub: pendingApprovalsCount === 0 ? 'All caught up' : 'Awaiting your decision',         icon: 'ClockIcon',                    color: pendingApprovalsCount > 0 ? '#F5A623' : '#16a34a', bg: pendingApprovalsCount > 0 ? '#fef3c7' : '#dcfce7' },
  ];

  return (
    <div className="mb-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            role="group"
            aria-label={tile.label}
            className="bg-white shadow-sm border border-gray-100 p-4 transition-all"
            style={{ borderRadius: 12 }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: tile.bg }}
                aria-hidden="true"
              >
                <Icon name={tile.icon as any} size={16} style={{ color: tile.color }} />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 truncate">{tile.label}</span>
            </div>
            <div className="text-2xl font-bold mt-3" style={{ color: tile.color }}>
              {tile.value}
            </div>
            <p className="text-[11px] text-gray-500 leading-tight mt-1.5 truncate" title={tile.sub}>
              {tile.sub}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HrKpiStrip;
